// ALL balance numbers live here. Logic must never hardcode balance values.
// Values are starting points from the v1 spec; tune with the balance harness (M8).

export const TUNABLES = {
  clock: {
    ticksPerDay: 1440, // 1 tick = 1 in-game minute
    realSecondsPerDay: 180, // at 1x speed
    speeds: [0, 1, 2, 4] as const,
    maxTicksPerFrame: 64, // avoid a catch-up spiral after a long frame
    // SPEC-NOTE: a new game and each Sleep start the day at 08:00 so you don't wake at midnight.
    // The world pre-simulates 00:00-08:00 of day 1 so charts already have history.
    wakeMinute: 8 * 60,
  },

  origins: {
    weights: { novice: 0.75, kol: 0.25 },
    novice: { cash: 0, followers: 12, trust: 50 },
    kol: { cash: 40_000, followers: 50_000, trust: 60 },
  },

  pfp: {
    rerolls: 2,
    gridSize: 6,
    // Small, cosmetic-leaning perk: extra starting followers + "PFP flex" badge.
    followerBonus: { Common: 0, Uncommon: 0, Rare: 0.1, Epic: 0.1, Legendary: 0.2 } as Record<string, number>,
    flexBadgeFrom: "Rare",
  },

  // DGN gas (production roadmap §1-2). Small fees per on-chain action; earned from check-ins and quests.
  gas: {
    start: 20, // DGN in a new wallet
    priceWusd: 0.5, // swap rate: 1 DGN = W$0.50
    swapSpread: 0.02, // Pumpr keeps 2% each way
    minSwapDgn: 1,
    checkIn: { base: 8, perStreakDay: 2, maxStreak: 7 }, // day 1: 8 DGN ... day 7+: 20 DGN
    questReward: 6, // DGN per tutorial quest
    // Fee per command. Commands not listed are free (walking, posting, coffee, houses, cars...).
    fees: {
      BUY_TOKEN: 0.4,
      SELL_TOKEN: 0.4,
      MINT_NFT: 1.5,
      LIST_NFT: 0.3,
      SELL_NFT_NOW: 0.4,
      LAUNCH_COIN: 5,
      LAUNCH_COLLECTION: 5,
      LAUNCH_AIRDROP: 3,
      ENTER_PRESALE: 0.5,
      SWAP_DGN_SELL: 0.2, // selling DGN for WUSD; buying DGN is always free so you can't get stuck
      HACK_BUILD: 0.3, // pushing a build to testnet
    } as Record<string, number>,
    // Farm tasks: testnets are free (that's the point of a testnet); bridge and swap tasks burn gas.
    farmFees: { bridge: 0.8, swap: 0.5 } as Record<string, number>,
  },

  // Vano: a paid trading bot (in-game WUSD, billed daily). Its edge is real but never perfect.
  vano: {
    tiers: {
      lite: { name: "Vano Lite", dailyWusd: 60, accuracy: 0.62, rugRadarTicks: 0, autoSnipe: false },
      pro: { name: "Vano Pro", dailyWusd: 250, accuracy: 0.74, rugRadarTicks: 30, autoSnipe: false },
      whale: { name: "Vano Whale", dailyWusd: 900, accuracy: 0.85, rugRadarTicks: 60, autoSnipe: true },
    } as Record<string, { name: string; dailyWusd: number; accuracy: number; rugRadarTicks: number; autoSnipe: boolean }>,
    falseAlarmPerHour: 0.03, // rug radar crying wolf on a coin you hold (scaled by 1 - accuracy)
    snipeMaxWusd: 2_000,
    judgeAfterTicks: 720, // calls are graded 12h after launch
    keepSignals: 40,
  },

  hackathons: {
    everyDays: 4, // a new one is announced every few days
    firstAnnounceDay: 2, // day 1 has no morning boundary (the game starts mid-day)
    announceDaysBefore: 1,
    durationDays: 2,
    entryDgn: 5, // gas to register (deploying your repo onchain, obviously)
    buildDgn: 0.3,
    buildsPerDay: 3,
    buildPoints: [6, 16] as [number, number], // per build session, before skill
    skillBonus: 0.08, // +8% per past hackathon, up to 10
    teams: [14, 40] as [number, number],
    npcMeanPoints: 78, // tuned: full grind on the hot track ≈ 30% podium / 17% win; other tracks ≈ 10% / 2%
    npcSdPoints: 16,
    hotTrackBonus: 1.1, // the judges' favourite track
    demoDayLuck: [0.8, 1.3] as [number, number], // judging multiplier: live demos are a coin flip
    prize: [3_000, 25_000] as [number, number],
    split: [0.5, 0.3, 0.2], // 1st / 2nd / 3rd
    followers: { win: 1_500, podium: 600, mention: 150 },
    trust: { win: 4, podium: 2 },
  },

  // Perps (§8): synthetic leveraged positions on live tokens, margined in DGN. They don't move the
  // market. Liquidations are a heat source.
  perps: {
    maxLeverage: 10,
    minMarginDgn: 2,
    feeRate: 0.001, // open/close fee on notional
    fundingPerHour: 0.0002, // of notional, paid in DGN
    maintenance: 0.1, // liquidated when equity <= 10% of margin
    liquidationHeat: 8,
    maxOpen: 5,
  },

  quests: {
    daily: 3,
    weekly: 2,
    dailyDgn: [4, 10] as [number, number],
    weeklyDgn: [25, 60] as [number, number],
  },

  // Giving people rides (cars, bikes, the Danfo): a fare for the distance, a lucky tip sometimes.
  rides: {
    maxPaidPerDay: 8,
    minMeters: 120, // shorter hops are a favor, no fare
    maxMeters: 30_000,
    perKm: 6, // W$ per km for one passenger in a normal car
    base: 2,
    fareCap: 80,
    bikeMul: 0.8, // okada fares are cheaper...
    vanMul: 0.7, // ...and so is a seat in the Danfo
    tierMul: { normal: 1, sport: 1.3, luxury: 1.8, exotic: 2.6, super: 3.5 } as Record<string, number>,
    tipChance: 0.25,
    tipRange: [0.2, 1] as [number, number],
    jackpotChance: 0.02, // a whale in the back seat
    jackpotMul: 6,
    flexChance: 0.15, // passenger posts the ride (luxury and up)
    flexFollowers: [20, 400] as [number, number],
  },

  phones: {
    flexFollowers: 0.01, // buying a phone flexes: +1% followers (cap below)
    flexCap: 400,
  },

  player: {
    // No energy system (removed 2026-10-04): DGN gas and diminishing returns pace the day instead.
    startHeat: 0,
    heatDecayPerDay: 15,
    layLowHeatDecayMultiplier: 2,
    // Calm-down actions: they cool Heat instead of refilling energy.
    coffee: { costUsd: 5, heat: -5, maxPerDay: 2 },
    touchGrass: { heat: -10, trust: 1, maxPerDay: 2 },
    cook: { costUsd: 12, heat: -8 },
    // Buying a very young token counts as "aping unknowns" (heat source).
    apeUnknown: { maxAgeTicks: 60, heat: 2 },
  },

  clout: { base: 0.25, trustWeight: 0.75 },

  rank: {
    names: ["Plankton", "Shrimp", "Crab", "Fish", "Dolphin", "Shark", "Whale"] as const,
    netWorth: [0, 250, 1_000, 5_000, 25_000, 150_000, 1_000_000],
    followers: [0, 100, 500, 2_500, 15_000, 75_000, 300_000],
    kolBadge: { followers: 15_000, trust: 50 },
  },

  market: {
    totalSupply: 1_000_000_000,
    tradeFee: 0.01, // 1% per trade, a pure sink
    ammFee: 0.003, // 0.3% pool fee, stays in the pool
    curve: {
      vUsd0: 5_000,
      vTokens0: 1_073_000_000,
      curveSupply: 800_000_000, // max tokens sellable from the curve
    },
    graduationMcap: 60_000,
    dustTokens: 1, // balances below this are removed from holder lists
    candleCap: 1_440, // rolling 1-minute candles kept in memory
    candleCapOnSave: 240,
    tradesCap: 30, // recent trades kept per token
    deadMcap: 6_000, // below this for deadAfterTicks -> token dies
    deadAfterTicks: 2 * 1_440,
    deadKeepDays: 3, // dead tokens nobody important holds are pruned after this
    maxActiveTokens: 25,
    spawnPerDay: [3, 8] as [number, number],
    initialTokens: 8, // live NPC tokens seeded at world creation
    // 2019-2024 bull-run feel (user direction 2026-10-04): lots of winners, few rugs, real 0 -> hero moments.
    fates: { normal: 0.5, runner: 0.28, rug: 0.08, slow_bleed: 0.14 },
    moonshotShare: 0.25, // share of runners that go parabolic (100x+ peaks)
    // Outside capital chasing moonshots (the "new money" of a bull run). Exits after the hype window.
    fomo: { targetMultiple: [80, 300] as [number, number], minHype: 40, buyChancePerTick: 0.06, buyShare: [0.01, 0.035] as [number, number], maxBuyUsd: 400_000, sellChancePerTick: 0.03, sellShare: 0.12 },
    // Dev buy at launch, as % of total supply. Big dev bags are a readable tell.
    devBagPct: {
      normal: [2, 15],
      runner: [2, 8],
      rug: [15, 40],
      slow_bleed: [5, 15],
    } as Record<string, [number, number]>,
    hype: {
      initial: {
        normal: [28, 55],
        runner: [55, 85],
        rug: [40, 75],
        slow_bleed: [8, 22],
      } as Record<string, [number, number]>,
      decayPerTick: { normal: 0.0028, runner: 0.001, rug: 0.003, slow_bleed: 0.01 } as Record<string, number>,
      buyImpact: 35, // hype += buyImpact * usd / mcap
      sellImpact: 60,
      playerTokenDecayPerTick: 0.003,
      runnerBoost: { chancePerTick: 0.012, amount: [8, 22] as [number, number], forTicks: 2 * 1_440 },
      moonshotBoost: { chancePerTick: 0.02, amount: [12, 30] as [number, number], forTicks: 4 * 1_440 },
    },
    rug: {
      delayTicks: [30, 720] as [number, number], // when after launch the dev dumps
      chunks: 3, // dev bag sold over this many ticks
    },
    normalDevSell: { chancePerTick: 0.002, fraction: 0.25, minPriceMultiple: 2 },
    slowBleedDevSell: { everyTicks: 60, fraction: 0.04 },
    shillTtlTicks: 120,
    npcShill: {
      runnerChancePerTick: 0.01,
      rugChancePerTick: 0.008, // rug devs pay bots to shill before the dump
      strength: [0.3, 0.9] as [number, number],
    },
    sentiment: {
      stepEveryTicks: 60,
      volatility: 0.06,
      meanReversion: 0.08,
      regimeShiftChancePerDay: 0.12,
      regimeDays: [2, 5] as [number, number],
      targets: { normal: 0.15, bull: 0.75, crash: -0.45 }, // mildly bullish baseline, shallower crashes
      bullShare: 0.7, // when the regime shifts: bull run vs crash
      buyMultiplier: 0.9, // buy probability *= 1 + sentiment * this
    },
  },

  npc: {
    count: 300,
    evalFraction: 0.1, // share of NPCs that make one decision per tick
    accounts: 60, // NPC Chirp accounts that can shill (full social sim in M4)
    followsPerCopytrader: [3, 10] as [number, number],
    archetypes: {
      sniper: 0.05,
      degen: 0.3,
      paper: 0.2,
      diamond: 0.1,
      whale: 0.03,
      copy: 0.17,
      farmer: 0.1,
      bot: 0.05,
    },
    startCash: { default: [200, 3_000] as [number, number], whale: [50_000, 250_000] as [number, number] },
    dailyStipend: { default: [40, 160] as [number, number], whale: [2_000, 8_000] as [number, number] },
    maxCash: { default: 6_000, whale: 400_000 },
    maxPositions: 5,
    minTradeUsd: 5,
    // Per-archetype decision parameters.
    sniper: { buyChance: 0.6, maxTokenAge: 8, size: [0.05, 0.15] as [number, number], takeProfit: [2, 4] as [number, number], maxHoldTicks: 60 },
    degen: { buyChance: 0.18, hypeThreshold: [15, 45] as [number, number], size: [0.08, 0.3] as [number, number], holdTicks: [20, 240] as [number, number], panicDrawdown: 0.3, takeProfit: 2 },
    paper: { buyChance: 0.14, minHype: 15, size: [0.05, 0.2] as [number, number], stopFromPeak: 0.15, holdTicks: [60, 400] as [number, number] },
    diamond: { buyChance: 0.06, minAgeTicks: 120, minHype: 25, size: [0.1, 0.3] as [number, number], dipAdd: 0.3, takeProfit: 10, deadHype: 3 },
    whale: { evalScale: 0.3, buyChance: 0.05, minMcap: 15_000, size: [0.005, 0.03] as [number, number], holdTicks: [120, 1_440] as [number, number], takeProfit: 3, stopLoss: 0.4 },
    copy: { reactChance: 0.5, size: [0.05, 0.2] as [number, number], holdTicks: [30, 300] as [number, number], panicDrawdown: 0.25, takeProfit: 1.8 },
    farmer: { buyChance: 0.02, size: [0.02, 0.08] as [number, number], holdTicks: [60, 600] as [number, number] },
    bot: { actChance: 0.7, usd: [10, 120] as [number, number] },
  },

  social: {
    // reach = (clout + baseReach)^reachExp * typeMult * sentimentFit * random(0.5, 1.5)
    baseReach: 250,
    reachExp: 0.7,
    conversion: 1.0, // newFollowers = engagement * conversion * trust/100 / (1 + followers/saturation)
    followerSaturation: 50_000, // big accounts grow slower in % terms
    sameTypeDiminish: 0.5, // mult /= 1 + this * (same-type posts already today)
    gmStreakBonusPerDay: 0.1,
    gmStreakMaxDays: 7,
    types: {
      gm: { mult: 0.6, quality: [0.7, 1.2] },
      meme: { mult: 1.0, quality: [0.2, 3] }, // high variance
      alpha: { mult: 1.2, quality: [0.6, 1.4] },
      fud: { mult: 1.0, quality: [0.6, 1.4] },
      shill: { mult: 0.9, quality: [0.6, 1.3] },
      thread: { mult: 0.4, quality: [0.8, 1.2] },
      callout: { mult: 1.3, quality: [0.5, 1.6] },
      reply: { mult: 0.3, quality: [0.6, 1.4] },
    } as Record<string, { mult: number; quality: [number, number] }>,
    sentimentFit: 0.3, // bullish posts do better in bull markets, FUD in crashes
    call: {
      resolveTicks: 1_440, // alpha / FUD calls are judged 24h later
      alphaWinPct: 0.1, // price +10% => right
      alphaLosePct: -0.2, // price -20% => wrong
      fudWinPct: -0.15,
      fudLosePct: 0.1,
      trustWin: 3,
      trustLose: -4,
      followerBonusMult: 0.6, // extra followers on a correct call, x original engagement x conversion
      hypeBoost: 15, // immediate hype change, scaled by reach
      fudHypeHit: 12,
      fudSellChance: 0.5, // holders' chance to dump on a FUD from you, scaled by reach
    },
    shill: { maxPerDay: 2, overLimitTrust: -2, sellWithinTicks: 360, sellAfterShillTrust: -5 },
    thread: { trust: 1.5 },
    callout: { winBase: 0.3, rugBonus: 0.4, trustWeight: 1 / 200, winTrust: 3, loseTrust: -3, loseFollowersPct: 0.03, winFollowersMult: 1.5 },
    reply: { reachFactor: 0.03 }, // extra reach = sqrt(target followers) * this
    decay: { kolThreshold: 15_000, kolPct: 0.01, smallPct: 0.002 },
    npcPostsPerHour: [2, 5] as [number, number],
    npcFollowers: [100, 2_000_000] as [number, number],
    feedCap: 150,
    engagementRevealTicks: 60, // likes/reposts tick up over the first in-game hour
  },

  airdrops: {
    liveProtocols: [6, 8] as [number, number],
    willAirdropChance: 0.7,
    tgeInDays: [5, 20] as [number, number],
    initialTgeInDays: [3, 15] as [number, number],
    allocationPoolUsd: [40_000, 300_000] as [number, number],
    sybilStrictness: [0.2, 0.9] as [number, number],
    npcPointsPerDay: [150, 500] as [number, number], // all NPC farmers combined, per protocol
    tasks: {
      testnet: { costUsd: 0, points: 15, minCash: 0, lowEffort: true },
      bridge: { costUsd: 5, points: 30, minCash: 20, lowEffort: false },
      swap: { costUsd: 3, points: 22, minCash: 0, lowEffort: false },
      social: { costUsd: 0, points: 8, minCash: 0, lowEffort: true },
      checkin: { costUsd: 0, points: 3, minCash: 0, lowEffort: true },
    } as Record<string, { costUsd: number; points: number; minCash: number; lowEffort: boolean }>,
    checkinStreakDays: 5,
    checkinStreakMult: 1.5,
    socialQuestFollowers: 0.3, // x normal post reach
    // Sybil: farming more than N protocols in a day with only low-effort tasks raises your score.
    sybilMaxProtocolsPerDay: 3,
    sybilDecayPerCleanDay: 0.25,
    sybilScoreForMaxRisk: 3,
    // TGE token: airdrop = 10% of supply; AMM liquidity = pool share of allocation value.
    // Luck-based TGE (one shot per protocol): farm enough to be eligible, then the draw decides.
    minEligiblePoints: 30,
    luckPointsForMax: 300, // points needed for the best odds
    // Tier odds at zero / max luck, and payout as a share of the allocation pool.
    tiers: {
      none: { odds: [0.5, 0.25], share: [0, 0] },
      small: { odds: [0.33, 0.33], share: [0.008, 0.025] },
      big: { odds: [0.13, 0.28], share: [0.04, 0.09] },
      jackpot: { odds: [0.04, 0.14], share: [0.15, 0.3] },
    } as Record<string, { odds: [number, number]; share: [number, number] }>,
    tgeAirdropShare: 0.1,
    tgeLiquidityShare: 0.5,
    tgeNpcDumpShare: 0.5, // share of NPC allocation that lands in NPC wallets that dump fast
    tgeNpcDumpers: 12,
  },

  nft: {
    dropsPerDay: [1, 3] as [number, number],
    supplies: [500, 1_000, 3_333, 10_000],
    mintPriceUsd: [20, 400] as [number, number],
    wlDiscount: 0.3,
    mintStartHour: [9, 20] as [number, number],
    mintDurationTicks: [120, 240] as [number, number],
    hype: [10, 85] as [number, number],
    maxPerWallet: 3,
    mintOutFloorMult: [1.5, 6] as [number, number],
    failFloorMult: [0.3, 0.9] as [number, number],
    floorVolatility: 0.04, // hourly
    floorSpikeChancePerHour: 0.01,
    hypeDecayPerHour: 0.97,
    marketFee: 0.025,
    royalty: 0.05,
    instantSellDiscount: 0.1, // top bid = floor x 0.9
    listingFillBase: 0.02, // per tick, scaled by hype and how cheap the listing is
    keepDays: 7,
  },

  presale: {
    chancePerLaunch: 0.4, // share of NPC launches that open a presale draw
    announceTicksBefore: 240, // draws open this long before launch (or when the day is scheduled)
    winChance: [0.18, 0.4] as [number, number], // grows with your reach
    bagUsd: [150, 1_500] as [number, number], // free allocation at launch price
  },

  launch: {
    coinFeeUsd: 50,
    hypeBase: 35, // fresh coins draw degen attention even from tiny accounts
    hypeReach: 45, // + reach factor (clout/(clout+5000)) x this
    hypeRandom: 15,
    rugWindowTicks: 48 * 60, // dumping the dev bag inside 48h counts as a rug
    rugSellShare: 0.5, // selling >= half of the dev bag in that window
    rugTrust: [25, 40] as [number, number],
    rugHeat: 40,
    sleuthChanceBase: 0.35, // + heat/200
    sleuthDelayTicks: [120, 720] as [number, number],
    exposedFollowersPct: [0.2, 0.5] as [number, number],
    exposedTrust: 30,
    banDays: 7,
    honestHoldShare: 0.7, // still holding >= 70% of the dev bag counts as building
    honestTrustPerDay: 0.6,
    honestDays: 7,
    updateHypeBump: 8,
    airdropPct: [1, 20] as [number, number],
    airdropTrust: [1, 6] as [number, number],
    airdropHype: 10,
    airdropDumpShare: 0.4,
    collectionFeeUsd: 200,
    collectionSupplies: [500, 1_000, 3_333, 10_000],
    collectionMintDelayTicks: 60,
    collectionMintTicks: 240,
    royaltyDays: 14,
    abandonDays: 5,
    abandonTrust: 6,
  },

  events: {
    maxPending: 2, // choice popups queued at most
    graceTicks: 1440, // no surprise popups on your first day (calm start; the tutorial's scam lesson still runs)
    phishingBasePerHour: 0.01,
    phishingHeatPerHour: 0.04, // x heat/100
    fakeClaimPerHour: 0.006,
    phishLoss: [0.3, 1] as [number, number], // share of cash drained (tokens are safe)
    phishHeat: 5,
    reportTrust: 1,
    exploitPerHour: 0.003,
    viralBasePerHour: 0.004,
    viralCloutPerHour: 0.01, // x reach (clout / (clout + 5000))
    viralMult: 10,
    viralWindowTicks: 360, // which recent post can go viral
    collabPerHour: 0.012, // KOL-sized accounts only
    collabMinFollowers: 15_000,
    collabPayUsd: [400, 4_000] as [number, number], // scaled by follower count
    collabRugWindowTicks: 72 * 60,
    collabRugTrust: -10,
  },

  rekt: {
    maxNetWorth: 10,
    minPeakNetWorth: 100, // you can't be rekt if you never had anything (fresh novices)
    comebackFollowersKeep: 0.3,
    comebackTrust: 40,
    comebackDays: 7,
    comebackEngagement: 1.25,
  },

  tutorial: {
    novice: { cash: 15, followers: 5, trust: 0 },
    kol: { cash: 0, followers: 150, trust: 1 },
  },

  pets: {
    maxPets: 3,
    // price, and the small perk each kind gives while happy (happiness >= perkMinHappiness)
    kinds: {
      dog: { price: 150, perk: "followers", amount: 10 }, // dog-walk friends: +10 followers every morning
      cat: { price: 150, perk: "heat", amount: 5 }, // heat cools 5 more per day
      frog: { price: 100, perk: "meme", amount: 0.2 }, // memes +20% engagement
      hamster: { price: 60, perk: "farm", amount: 0.1 }, // farm task points +10%
      penguin: { price: 300, perk: "phish", amount: 0.5 }, // half as many scam DMs
      capybara: { price: 500, perk: "trust", amount: 0.2 }, // +0.2 trust per day (chill vibes)
    } as Record<string, { price: number; perk: string; amount: number }>,
    happinessStart: 80,
    happinessDecayPerDay: 12,
    feedCostUsd: 8,
    feedHappiness: 35,
    petHappiness: 6, // free, once per pet per day
    perkMinHappiness: 30,
    runAwayAfterSadDays: 3, // at 0 happiness for this many days, the pet leaves
  },

  save: {
    storageKey: "degen-world:save",
    logCap: 200,
  },
} as const;

export type Tunables = typeof TUNABLES;
